import MapErrorBoundary from "@/components/maps/MapErrorBoundary"
import MapView from "@/components/maps/MapView"
import { findRoute2g } from "@/reducers/routes2gSlice"
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "@/components/ui/resizable"
import TollsInformation from "@/components/maps/TollsInformation"
import { useEffect, useRef, useState } from "react"
import RouteOptionsSelect from "@/components/maps/RouteOptionsSelect"
import RouteStopList from "@/components/RouteStopList"
import { useAppSelector } from "@/reducers/hooks"
import { Button } from "../ui/button"
import { ArrowLeft } from "lucide-react"
import css from "../RouteView.module.css"
import { useMapToaster } from "@/hooks/useMapToaster"
import { useRouteTolls } from "@/hooks/useRouteTolls"
import { useTranslation } from "react-i18next"

type FullScreenMapProps = {
  driverJid: string
  locationJid?: string
  onCloseFullScreen: () => void
  onOpenFullScreen: () => void
  isReadOnlyMap?: boolean
}

const FullScreenMap = ({
  driverJid,
  locationJid,
  onCloseFullScreen,
  isReadOnlyMap = false,
}: FullScreenMapProps) => {
  const { t } = useTranslation()
  const route = useAppSelector(findRoute2g(driverJid))
  const [hasScroll, setHasScroll] = useState(false)
  const routeContainerRef = useRef<HTMLDivElement>(null)

  const tolls = useRouteTolls(driverJid, (route?.stops?.length ?? 0) > 0)

  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && onCloseFullScreen) {
        onCloseFullScreen()
      }
    }

    window.addEventListener("keydown", handleEscKey)

    return () => {
      window.removeEventListener("keydown", handleEscKey)
    }
  }, [onCloseFullScreen])

  // Add this effect to check for scroll
  useEffect(() => {
    const checkForScroll = () => {
      if (routeContainerRef.current) {
        const hasVerticalScroll =
          routeContainerRef.current.scrollHeight >
          routeContainerRef.current.clientHeight
        setHasScroll(hasVerticalScroll)
      }
    }

    // Check initially
    checkForScroll()

    // Add resize listener to check when window size changes
    window.addEventListener("resize", checkForScroll)

    // Create a mutation observer to watch for content changes
    const observer = new MutationObserver(checkForScroll)
    if (routeContainerRef.current) {
      observer.observe(routeContainerRef.current, {
        childList: true,
        subtree: true,
      })
    }

    return () => {
      window.removeEventListener("resize", checkForScroll)
      observer.disconnect()
    }
  }, [])

  useMapToaster(routeContainerRef.current)

  return (
    <div className="bg:white fixed inset-0 left-[48px] z-30 h-screen w-[calc(100vw-49px)] dark:bg-gray-900">
      <ResizablePanelGroup
        direction="horizontal"
        className="max-w-screen min-h-screen"
      >
        <ResizablePanel
          className="flex flex-col bg-[var(--panel-color)]"
          defaultSize={30}
          maxSize={50}
          minSize={25}
        >
          <div className="flex w-full flex-col p-4">
            <Button
              variant="ghost"
              onClick={onCloseFullScreen}
              className="self-start pl-0 text-sm"
            >
              <ArrowLeft className="h-4 w-4" />
              {t("common.back")}
            </Button>
            <h1 className="text-2xl font-bold">
              {t("maps.fullScreenMap.currentRoute")}
            </h1>
          </div>
          <div
            ref={routeContainerRef}
            className="relative z-10 flex h-full w-full flex-1 flex-col items-start justify-start overflow-x-auto px-4 pt-4"
          >
            <div className="flex h-full w-full flex-col">
              <div className="flex w-full flex-wrap gap-2">
                <div
                  className={`w-full space-y-4 pb-4 ${hasScroll ? css.showShadow : ""}`}
                >
                  {!isReadOnlyMap && (
                    <RouteOptionsSelect
                      className="!mx-0"
                      stops={route?.stops}
                      driverJid={driverJid}
                      onTollsToggle={tolls.toggle}
                      tollsToggleLabel={tolls.label}
                    />
                  )}
                  {!isReadOnlyMap && tolls.visible && (
                    <TollsInformation tolls={tolls.tolls} className="mx-0" />
                  )}
                  {!isReadOnlyMap && <div className="mb-4" />}
                  <div className="flex min-h-0 flex-1 flex-col">
                    <div className={`w-full space-y-4 pb-4`}>
                      <RouteStopList
                        driverJid={driverJid}
                        locationJid={locationJid}
                        isReadOnlyMap={isReadOnlyMap}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </ResizablePanel>
        <ResizableHandle withHandle />
        <ResizablePanel defaultSize={70}>
          <div className="relative flex h-full items-center justify-center">
            <MapErrorBoundary>
              <MapView
                isReadOnlyMap={isReadOnlyMap}
                isFullScreen
                driverJid={driverJid}
                locationJid={locationJid}
              />
            </MapErrorBoundary>
          </div>
        </ResizablePanel>
      </ResizablePanelGroup>
    </div>
  )
}

export default FullScreenMap
